import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import FeaturedAdsCarousel from '../components/FeaturedAdsCarousel';
import PopupAd from '../components/PopupAd';
import './HomePage.css';

function HomePage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const categoriesQuery = query(
        collection(db, 'categories'),
        where('isActive', '==', true)
      );
      const categoriesSnapshot = await getDocs(categoriesQuery);
      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      categoriesData.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(categoriesData);
      
      console.log('Fetched categories:', categoriesData.length);
    } catch (error) {
      console.error('Error fetching data:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home-page">
      <section className="hero">
        <img src="https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=2000&q=90" alt="" className="hero-background" />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="logo-section">
            <img src="/discover-logo.png" alt="Discover Cook Islands" className="hero-logo" />
            <h1 className="main-title">Discover Cook Islands</h1>
            <p className="subtitle">Your Ultimate Guide to Paradise</p>
          </div>
          <p className="hero-description">Explore the best businesses, attractions, dining, and experiences across the beautiful Cook Islands</p>
          <div className="hero-buttons">
            <Link to="/categories" className="btn btn-primary">
              <span>Explore Categories</span>
            </Link>
            <Link to="/map" className="btn btn-secondary">
              <span>View on Map</span>
            </Link>
          </div>
          <div className="download-app-section">
            <p className="download-text">Download our mobile app</p>
            <div className="app-store-buttons">
              <a 
                href="https://play.google.com/store" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="app-store-btn"
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" />
              </a>
              <a 
                href="https://www.apple.com/app-store/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="app-store-btn"
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="Download on the App Store" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading amazing experiences...</p>
        </div>
      ) : (
        <>
          <FeaturedAdsCarousel />
          <PopupAd />
          
          <section className="categories-section">
            <div className="section-header">
              <h2>Browse by Category</h2>
              <p>Find exactly what you're looking for</p>
            </div>
            <div className="categories-grid">
              {categories.map(category => (
                <Link
                  key={category.id}
                  to={`/category/${category.id}`}
                  className="category-card-new"
                >
                  {category.bannerUrl || category.imageUrl ? (
                    <div className="category-image-wrapper with-image">
                      <img 
                        src={category.bannerUrl || category.imageUrl} 
                        alt={category.name} 
                        className="category-image"
                      />
                    </div>
                  ) : (
                    <div className="category-image-wrapper">
                      {category.icon || category.name.charAt(0)}
                    </div>
                  )}
                  <div className="category-info">
                    <h3>{category.name}</h3>
                  </div>
                  <div className="category-arrow">→</div>
                </Link>
              ))}
            </div>
          </section>

          <section className="destination-showcase">
            <div className="section-header">
              <h2>Experience Paradise</h2>
              <p>Explore the stunning beauty of the Cook Islands</p>
            </div>
            <div className="showcase-grid">
              <div className="showcase-item">
                <img src="https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1200&q=90" alt="Cook Islands Beach" />
                <div className="showcase-overlay">
                  <h3>Crystal Clear Waters</h3>
                </div>
              </div>
              <div className="showcase-item">
                <img src="https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=1200&q=90" alt="Cook Islands Paradise" />
                <div className="showcase-overlay">
                  <h3>Tropical Paradise</h3>
                </div>
              </div>
              <div className="showcase-item">
                <img src="https://images.unsplash.com/photo-1602002418082-a4443e081dd1?w=1200&q=90" alt="Island Life" />
                <div className="showcase-overlay">
                  <h3>Island Adventures</h3>
                </div>
              </div>
            </div>
          </section>

          <section className="quick-links-section">
            <div className="section-header">
              <h2>Quick Access</h2>
              <p>Everything you need at your fingertips</p>
            </div>
            <div className="quick-links-grid">
              <Link to="/weather" className="quick-link-card">
                <div className="quick-link-icon-wrapper">🌤️</div>
                <div className="quick-link-info">
                  <h3>Weather</h3>
                  <p>Current conditions</p>
                </div>
              </Link>
              <Link to="/tools" className="quick-link-card">
                <div className="quick-link-icon-wrapper">🛠️</div>
                <div className="quick-link-info">
                  <h3>Useful Tools</h3>
                  <p>Currency & guides</p>
                </div>
              </Link>
              <Link to="/map" className="quick-link-card">
                <div className="quick-link-icon-wrapper">🗺️</div>
                <div className="quick-link-info">
                  <h3>Map View</h3>
                  <p>Explore visually</p>
                </div>
              </Link>
              <Link to="/favorites" className="quick-link-card">
                <div className="quick-link-icon-wrapper">❤️</div>
                <div className="quick-link-info">
                  <h3>Favorites</h3>
                  <p>Your saved places</p>
                </div>
              </Link>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export default HomePage;
